<template>
  <b-container fluid>
    <backlink />
    <h1>Commandes</h1>

    <AdminTable :config="config" :options="options" :items_provider="itemsProvider">
      <template slot="business_fullname" slot-scope="{ data }">
        <span class="text-red" v-if="externalAgencies.includes(data.value.agency.internal_number)">{{ data.value.business.number }} - {{ data.value.business.name }}</span>
        <span v-else>{{ data.value.business.number }} - {{ data.value.business.name }}</span>
      </template>
      <template slot="status" slot-scope="{ data }">
        <b-badge variant="primary">{{ data.value }}</b-badge>
      </template>
      <template slot="renting_type_slug" slot-scope="{ data }">
        <b-badge v-if="data.item.raw_renting_type_slug == 'exchange' || data.value == 'Échange'" variant="info">Échange</b-badge>
        <template v-else>{{ data.value }}</template>
      </template>
      <template slot="services" slot-scope="{ data }">
        <div style="width: 75px">
          <b-badge
            v-if="data.value.includes('outillage')"
            variant="outillage"
            class="circle float-left"
            v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('outillage').text"
          >
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge
            v-else
            variant="transparent"
            class="circle float-left"
            v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('outillage').text"
          >
            &nbsp;
          </b-badge>
          <b-badge
            v-if="data.value.includes('instruments')"
            variant="instruments"
            class="circle float-left"
            v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('instruments').text"
          >
            <i class="fa fa-check"></i>
          </b-badge>
          <b-badge
            v-else
            variant="transparent"
            class="circle float-left"
            v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('outillage').text"
          >
            &nbsp;
          </b-badge>
          <b-badge
            v-if="data.value.includes('service_et_logistique')"
            variant="service_et_logistique"
            class="circle float-left"
            v-b-tooltip.hover
            :title="$store.getters['renting_preparation/getGroup']('service_et_logistique').text"
          >
            <i class="fa fa-check"></i>
          </b-badge>
        </div>
      </template>
      <template slot="actions" slot-scope="{ data }">
        <b-button
          @click.prevent="renting_id = data.item.id"
          v-b-modal.modal-cancel
          v-b-tooltip.hover
          title="Annuler la demande"
          variant="outline-secondary"
          v-if="data.item.status != 'Annulée' && data.item.status != 'Terminée'"
        >
          <i class="far fa-times"></i>
        </b-button>
      </template>
    </AdminTable>

    <b-modal id="modal-cancel" hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">Annuler la demande</template>
      <div class="d-block text-center">
        <h3>Êtes-vous sur de vouloir annuler cette demande ?</h3>
      </div>
      <b-button class="mt-3" variant="success" block @click="cancel()">Confirmer</b-button>
      <b-button class="mt-3" variant="outline-secondary" block @click="cancelCancel()"
        >Annuler</b-button
      >
    </b-modal>
  </b-container>
</template>

<script>
import AdminTable from "@/components/Global/AdminTableSync";
import config from "./config";

/**
 * Onglets de la liste des commandes, dans l'ordre du workflow.
 *
 * `renting-en-attente-de-prix` (SFD 2.8 § Commandes Longue Durée) s'intercale juste après la
 * prise de commande : tant que le prix négocié n'est pas saisi, la préparation engagée ne peut
 * pas être lancée.
 */
const RENTING_STATUS_TABS = [
  "renting-non-prise-en-compte",
  "renting-en-attente-de-prix",
  "renting-en-attente-de-traitement",
  "renting-en-preparation",
  "renting-partially-shipped",
  "renting-terminee",
  "renting-annulee",
];

export default {
  name: "RentingList",
  components: {
    AdminTable,
  },
  data() {
    return {
      renting_id: null,
      externalAgencies: process.env.EXTERNAL_AGENCIES
    };
  },
  created() {
    // INC-001 : on charge la liste des entrepôts pour le filtre (utile seulement en accès-tout)
    if (this.isAllWarehouses) {
      this.$store.dispatch("warehouse/getAllForSelect");
    }
  },
  computed: {
    // INC-001 : true si l'utilisateur n'a pas d'entrepôt rattaché (accès à tous les entrepôts)
    isAllWarehouses: function () {
      return !this.$store.getters["auth/warehouse"];
    },
    // Config dérivée : on retire le filtre entrepôt pour les utilisateurs rattachés
    // à un entrepôt (périmètre déjà forcé côté serveur)
    config: function () {
      if (this.isAllWarehouses) {
        return config;
      }
      return { ...config, fields: config.fields.filter((field) => field.name !== "warehouse") };
    },
    options: function () {
      let status_options = this.$store.getters["status/getByGroup"]("renting").selectOptions(
        "status_slug",
        "name"
      );
      return {
        renting_type_slug_options: this.$store.getters["renting/getTypes"],
        // Onglets ordonnés selon le workflow, et non selon l'ordre rendu par l'API — qui trie
        // par libellé. La sélection se fait par slug : reposer sur l'index positionnel décalait
        // tous les onglets dès l'ajout d'un statut au groupe (« En attente de prix » s'intercale
        // alphabétiquement avant « En attente de traitement »).
        status_options: RENTING_STATUS_TABS.map((slug) =>
          status_options.find((option) => option.value === slug)
        ).filter(Boolean),
        days_before_shipped_options: [
          {
            value: 1,
            text: "De 0 à 1 jour",
          },
          {
            value: 2,
            text: "De 2 à 4 jours",
          },
          {
            value: 3,
            text: "Supérieur à 4 jours",
          },
        ],
        services_options: this.$store.getters["renting_preparation/getGroups"],
        from_mcs_options: [
          { value: null, text: "Toutes" },
          { value: '1', text: "Import MCS" },
          { value: '0', text: "Catalogue ESM" },
        ],
        // Longue durée (SFD 2.8) : isole les commandes engagées, qui se traitent à part.
        long_term_filter_options: [
          { value: null, text: "Toutes" },
          { value: '1', text: "Avec engagement" },
          { value: '0', text: "Sans engagement" },
        ],
        // Échanges (SFD Échanges et stockage 1.1) : suffixe -E, badge « Échange ».
        type_options: [
          { value: null, text: "Tous" },
          { value: 'standard', text: "Standard" },
          { value: 'exchange', text: "Échange" },
        ],
        // INC-001 : options du filtre entrepôt
        warehouse_options: (this.$store.getters["warehouse/getAll"] || []).map((w) => ({
          value: w.id,
          text: w.internal_code ? w.internal_code + " - " + w.name : w.name,
        })),
      };
    },
  },
  methods: {
    /**
     * Fourni les données à AdminTableSync. Cette méthode est appelée à chaque
     * rafraichissement de la liste
     * @param   {object}  ctx   Objet context envoyé par b-table
     * @returns {object}        {
                                  total: Nombre total de résultats,
                                  items: Items à afficher
                                }
     */
    itemsProvider(ctx) {
      var self = this;
      return new Promise(function (resolve, reject) {
        self.$http
          .get(process.env.BACK_API_URL + "renting", {
            params: {
              page: ctx.currentPage,
              limit: ctx.perPage,
              sortBy: ctx.sortBy,
              sortDesc: ctx.sortDesc ? "DESC" : "ASC",
              search: ctx.filter,
              statusSlug: ctx.extended_filters_model.status,
              createdAt1: ctx.extended_filters_model.axiloc_creation_date.start
                ? self
                    .$moment(ctx.extended_filters_model.axiloc_creation_date.start)
                    .format("YYYY-MM-DD")
                : null,
              createdAt2: ctx.extended_filters_model.axiloc_creation_date.end
                ? self
                    .$moment(ctx.extended_filters_model.axiloc_creation_date.end)
                    .format("YYYY-MM-DD")
                : null,
              wishedDeliveryDate1: ctx.extended_filters_model.wished_delivery_date.start
                ? self
                    .$moment(ctx.extended_filters_model.wished_delivery_date.start)
                    .format("YYYY-MM-DD")
                : null,
              wishedDeliveryDate2: ctx.extended_filters_model.wished_delivery_date.end
                ? self
                    .$moment(ctx.extended_filters_model.wished_delivery_date.end)
                    .format("YYYY-MM-DD")
                : null,
              daysBeforeShipped: ctx.extended_filters_model.days_before_shipped,
              groupSlug: ctx.extended_filters_model.services,
              from_mcs: ctx.extended_filters_model.from_mcs,
              // Longue durée (SFD 2.8) : commandes portant au moins une ligne engagée.
              long_term: ctx.extended_filters_model.long_term_filter,
              // INC-001 : filtre entrepôt (ignoré côté serveur si l'utilisateur est déjà scopé)
              warehouse: ctx.extended_filters_model.warehouse,
              type: ctx.extended_filters_model.type,
            },
          })
          .then((response) => {
            resolve({
              total: response.data.total,
              page: parseInt(response.data.page),
              items: response.data.rentings || [],
            });
          })
          .catch((error) => {
            self.$toastr.error("Le chargement de la liste a échoué");
            self.inGenerating = false;
          });
      });
    },
    /**
     * Change le statut de la demande pour 'Annulée'
     */
    cancel() {
      this.$store
        .dispatch("renting/getItem", {
          id: this.renting_id,
          wait: true,
        })
        .then(() => {
          var item = this.$store.getters["renting/getItem"](this.renting_id);

          item = {
            ...item,
            agency_number: item.agency ? item.agency.internal_number : null,
            business_number: item.business ? item.business.number : null,
            employee_number: item.employee ? item.employee.number : null,
            user_id: item.user ? item.user.id : null,
            status_slug: "renting-annulee",
            withdrawed_at_agency: item.withdrawed_at_agency ? item.withdrawed_at_agency : false,
          };

          this.$store.dispatch(this.config.storeNamespace + "/saveItem", item).then(() => {
            this.$toastr.success("La commande a été annulée");
            this.$root.$emit("bv::hide::modal", "modal-cancel");
          });
        });
    },
    cancelCancel() {
      this.$root.$emit("bv::hide::modal", "modal-cancel");
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/_variables.scss";

.text-red {
  color: $red;
}
</style>



// WEBPACK FOOTER //
// src/components/Renting/RentingList.vue